import React, { useState, useEffect, Fragment } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { IconButton } from '@radix-ui/themes';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import Logo from '../common/Logo';
import LogoutModal from '../common/LogoutModal';
import { providersAPI } from '../../api/providers';
import {
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ArrowRightIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/search', label: 'Find Providers' },
  { to: '/#services', label: 'Services' },
  { to: '/about', label: 'About' },
];

const Navbar = () => {
  const { user, isAuthenticated, logout, isProvider, isAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isAuthenticated && isProvider) {
      providersAPI
        .getMyProfile()
        .then((data) => {
          if (data?.photoUrl) setProfilePhoto(data.photoUrl);
        })
        .catch(() => {});
    }
    const handlePhotoUpdate = (e) => setProfilePhoto(e.detail);
    window.addEventListener('profilePhotoUpdated', handlePhotoUpdate);
    return () => window.removeEventListener('profilePhotoUpdated', handlePhotoUpdate);
  }, [isAuthenticated, isProvider]);

  const handleLogout = () => setShowLogoutModal(true);
  const confirmLogout = () => {
    logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  const getDashboardPath = () => {
    if (isAdmin) return '/admin/dashboard';
    if (isProvider) return '/provider/dashboard';
    return '/bookings';
  };

  const avatarSrc = profilePhoto || user?.avatarUrl;

  const isActive = (to) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <>
    <LogoutModal
      isOpen={showLogoutModal}
      onConfirm={confirmLogout}
      onCancel={() => setShowLogoutModal(false)}
    />
    <nav
      className="fixed top-0 left-0 right-0 z-40 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255,255,255,0.92)' : '#fff',
        backdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
        boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.06)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.04)' : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between sm:h-16 lg:h-[72px]">

          {/* Logo */}
          <div className="flex-shrink-0">
            <Logo size="large" dark />
          </div>

          {/* Desktop Nav links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="relative px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  color: isActive(to) ? 'var(--orange-9)' : 'var(--gray-12)',
                  fontFamily: "'Poppins', sans-serif",
                }}
                onMouseEnter={(e) => {
                  if (!isActive(to)) e.currentTarget.style.color = 'var(--orange-9)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive(to)) e.currentTarget.style.color = 'var(--gray-12)';
                }}
              >
                {label}
                {isActive(to) && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-5 h-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--orange-9)' }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-2">
            {/* DARK MODE TEMPORARILY DISABLED — restore from git history to bring back */}
            {false && (
              <IconButton
                variant="ghost"
                size="2"
                onClick={toggleTheme}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{ color: 'var(--gray-11)', cursor: 'pointer' }}
              >
                {isDark ? (
                  <SunIcon style={{ width: '18px', height: '18px' }} />
                ) : (
                  <MoonIcon style={{ width: '18px', height: '18px' }} />
                )}
              </IconButton>
            )}

            {isAuthenticated ? (
              <Menu as="div" className="relative ml-2">
                <Menu.Button className="flex items-center gap-2 p-1 pr-3 rounded-full transition-colors hover:bg-gray-100">
                  {avatarSrc ? (
                    <img
                      src={avatarSrc}
                      alt={user?.name}
                      style={{
                        width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '34px', height: '34px', borderRadius: '50%',
                        backgroundColor: 'var(--orange-3)', color: 'var(--orange-10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '14px', fontWeight: 700,
                      }}
                    >
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-800" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {user?.name?.split(' ')[0] || 'Account'}
                  </span>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg border border-gray-100 overflow-hidden py-2">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to={getDashboardPath()}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm"
                          style={{
                            backgroundColor: active ? 'var(--orange-2)' : 'transparent',
                            color: 'var(--gray-12)',
                          }}
                        >
                          <Squares2X2Icon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                          Dashboard
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm"
                          style={{
                            backgroundColor: active ? 'var(--orange-2)' : 'transparent',
                            color: 'var(--gray-12)',
                          }}
                        >
                          <UserCircleIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                          Profile
                        </Link>
                      )}
                    </Menu.Item>
                    <div className="my-1 h-px bg-gray-100" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-left"
                          style={{
                            backgroundColor: active ? 'var(--orange-2)' : 'transparent',
                            color: 'var(--gray-12)',
                          }}
                        >
                          <ArrowLeftOnRectangleIcon className="w-4 h-4" style={{ color: 'var(--orange-9)' }} />
                          Log out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-12)')}
                >
                  Sign In
                </Link>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
                  style={{
                    backgroundColor: 'var(--orange-9)',
                    color: '#fff',
                    fontFamily: "'Poppins', sans-serif",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-10)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-9)')}
                >
                  Book Now
                  <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-2">
            {/* DARK MODE TEMPORARILY DISABLED — restore from git history to bring back */}
            {false && (
              <IconButton
                variant="ghost"
                size="2"
                onClick={toggleTheme}
                style={{ color: 'var(--gray-11)' }}
              >
                {isDark ? (
                  <SunIcon style={{ width: '18px', height: '18px' }} />
                ) : (
                  <MoonIcon style={{ width: '18px', height: '18px' }} />
                )}
              </IconButton>
            )}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-1.5"
              style={{ color: 'var(--gray-12)' }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <Transition
        show={mobileMenuOpen}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <div className="max-h-[calc(100dvh-56px)] overflow-y-auto border-t border-gray-100 bg-white shadow-lg lg:hidden">
          <div className="mx-auto max-w-7xl space-y-1 px-3 py-2.5">
            {navLinks.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMobileMenuOpen(false)}
                className="block rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                style={{
                  color: isActive(to) ? 'var(--orange-9)' : 'var(--gray-12)',
                  backgroundColor: isActive(to) ? 'var(--orange-2)' : 'transparent',
                  fontFamily: "'Poppins', sans-serif",
                }}
              >
                {label}
              </Link>
            ))}

            <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardPath()}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium"
                    style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-2 text-sm font-medium"
                    style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium"
                    style={{ color: 'var(--red-10)', fontFamily: "'Poppins', sans-serif" }}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex min-h-9 items-center justify-center rounded-md border px-2 py-1.5 text-center text-xs font-medium"
                    style={{
                      color: 'var(--gray-12)',
                      borderColor: 'var(--gray-5)',
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/search"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex min-h-9 items-center justify-center rounded-md px-2 py-1.5 text-center text-xs font-semibold"
                    style={{
                      backgroundColor: 'var(--orange-9)',
                      color: '#fff',
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Book Now
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </Transition>
    </nav>
    </>
  );
};

export default Navbar;
